// Alert destinations: where the organization's findings are sent, what is
// configured, and how to add one. (Not a person's own notifications, which
// live under their account.)
//
// The list is the page. Adding used to be an always-open form beneath it with
// every field rendered and Slack preselected, which is the shape
// `OverlayDialog` was written to replace -- so adding opens a dialog and asks
// nothing until a destination is chosen.

import { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../ConfirmDialog.jsx";
import SectionActions from "../settings/SectionActions.jsx";
import Button from "../ui/Button.jsx";
import Listing from "../ui/Listing.jsx";
import { ErrorText } from "../ui/Notice.jsx";
import { Count } from "../ui/TableBar.jsx";
import { listSignals } from "../../lib/signalsApi.js";
import { restGet, restPost } from "../../lib/restClient.js";
import AddDestinationDialog from "./AddDestinationDialog.jsx";
import DestinationList, { vendorNames } from "./DestinationList.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

export default function PushNotificationsPanel({ canManage = true }) {
    // null until the first load answers; a refetch keeps the last list on screen.
    const [integrations, setIntegrations] = useState(null);
    const [loading, setLoading] = useState(true);
    // Two separate failures, because they mean opposite things about the list. An
    // action that failed leaves the list valid and on screen; a load that failed
    // means we do not know what the tenant has, and rendering the empty state
    // then asserts it has nothing.
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [signals, setSignals] = useState([]);
    const [adding, setAdding] = useState(false);
    const [pendingRemove, setPendingRemove] = useState(null);

    const refetch = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await restGet("/v1/push-notification-integrations");
            setIntegrations(resp?.integrations ?? []);
            setLoadError(null);
        } catch (err) {
            setLoadError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refetch();
    }, [refetch]);

    // Only detections publish notifications, so only detections can be picked.
    // A failure here leaves the picker empty rather than taking the page down —
    // the wildcard still works without tenant.signals.read.
    useEffect(() => {
        let cancelled = false;
        listSignals()
            .then((all) => {
                if (!cancelled) setSignals(all.filter((s) => s.kind === "detection"));
            })
            .catch(() => {
                if (!cancelled) setSignals([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const signalNamesById = useMemo(
        () => Object.fromEntries(signals.map((s) => [s.id, s.displayName || s.name])),
        [signals],
    );

    const onAdd = useCallback(
        async (payload) => {
            await restPost("/v1/push-notification-integrations", payload);
            // A failure from an earlier action is stale once a create succeeds.
            setError(null);
            await refetch();
            // The dialog closes on success, so the confirmation belongs here.
            // It stays until the next action rather than timing out; nothing
            // else on this panel disappears on its own.
            setNotice("Destination added");
        },
        [refetch],
    );

    const onToggle = useCallback(
        async (integration) => {
            setError(null);
            setNotice(null);
            try {
                await restPost("/v1/push-notification-integrations:set-enabled", {
                    integration_id: integration.id,
                    enabled: !integration.enabled,
                });
                await refetch();
            } catch (err) {
                setError(err.message || String(err));
            }
        },
        [refetch],
    );

    const onRemove = useCallback(
        async (integration) => {
            setError(null);
            setNotice(null);
            try {
                await restPost("/v1/push-notification-integrations:delete", {
                    integration_id: integration.id,
                });
            } catch (err) {
                // 404 means it is already gone — someone removed it in another
                // session, or from the database directly. The list is simply
                // stale, and the user asked for exactly the state they now
                // have, so refresh rather than report a failure.
                if (err?.status !== 404) {
                    setError(err.message || String(err));
                    // Refetching here would clear the message it just set, so
                    // the failure would flash and vanish. The list is unchanged
                    // anyway -- the delete did not happen.
                    return;
                }
            }
            await refetch();
        },
        [refetch],
    );

    const openAdd = () => {
        setNotice(null);
        setAdding(true);
    };

    return (
        <section
            id="notifications-destinations"
            className="grid min-w-0 gap-[12px]"
            aria-busy={loading ? "true" : undefined}
        >
            <SectionActions>
                <Button className="shrink-0" aria-haspopup="dialog" onClick={openAdd}>
                    Add destination
                </Button>
            </SectionActions>

            {/* A failed load shows no count (it would claim to know what the
                tenant has); none shows the empty state, with the way to add one.
                The vendors come from the vendor table, so a new one is named. */}
            <Listing
                count={integrations?.length ?? null}
                error={loadError}
                errorTitle="Couldn't load destinations"
                onRetry={refetch}
                retrying={loading}
                bar={<Count n={integrations?.length ?? null} one="destination" />}
                barEnd={notice || error ? (
                    <>
                        {notice ? <span role="status" className="text-[12px] text-[color:var(--ink)]">{notice}</span> : null}
                        <ErrorText>{error}</ErrorText>
                    </>
                ) : null}
                empty={{
                    title: "No destinations yet",
                    note: `Use Add destination to send findings to ${vendorNames()} as they're found.`,
                }}
            >
                <DestinationList
                    loading={integrations === null}
                    integrations={integrations ?? []}
                    signalNamesById={signalNamesById}
                    canManage={canManage}
                    onToggle={onToggle}
                    onRemove={setPendingRemove}
                />
            </Listing>

            <AddDestinationDialog
                open={adding}
                onClose={() => setAdding(false)}
                onSubmit={onAdd}
                signals={signals}
            />

            <ConfirmDialog
                open={pendingRemove != null}
                analyticsId={DIALOG.NOTIFICATION_DESTINATION_REMOVE}
                eyebrow="Alert destinations"
                title={`Remove ${pendingRemove?.displayName ?? pendingRemove?.display_name ?? ""}?`}
                body="Findings stop going to this destination straight away. Its credentials are deleted, so you would need to enter them again to set it up later."
                confirmLabel="Remove"
                destructive
                onConfirm={() => {
                    const target = pendingRemove;
                    setPendingRemove(null);
                    if (target) onRemove(target);
                }}
                onCancel={() => setPendingRemove(null)}
            />
        </section>
    );
}
